import { useEffect, useState } from 'react';

import { useJournalAnalytics } from 'app/analytics/useJournalAnalytics';
import { Box, Flex, Loader, Show, For } from 'shared';
import { Nullable } from 'shared_DEPRECATED/types';

import {
  JournalReply,
  JournalRepliesCard,
  useJournalNoteQuery,
  JournalNote,
  JournalForm,
  useJournalEditNoteMutation,
  JournalNoteReply,
  JournalReplyCardReactions,
  useJournalCreateReplyMutation,
  useJournalEditReplyMutation,
  JournalFormHeader,
  JournalFormHeaderTitle,
  JournalCardUnreadIndicator,
  useJournalMarkNoteAsReadMutation,
} from 'features/journal';
import { User } from 'features/user/User';

import { useSidebarContext } from 'shared/components/Sidebar/context';

export const JournalRepliesContent = () => {
  const [editedNote, setEditedNote] = useState<Nullable<JournalNote>>(null);
  const [editedReply, setEditedReply] =
    useState<Nullable<JournalNoteReply>>(null);

  const { noteId, user, participantId } = useSidebarContext() as {
    user: User;
    noteId: string;
    participantId: string;
  };

  const { data: { note, replies } = {}, isLoading } = useJournalNoteQuery({
    noteId,
    participantId,
  });

  const {
    sendNoteReplyAddedAnalyticsEvent,
    sendNoteReplyEditedAnalyticsEvent,
  } = useJournalAnalytics();
  const { mutateAsync: editNote } = useJournalEditNoteMutation({
    participantId,
  });
  const { mutateAsync: editReply } = useJournalEditReplyMutation({
    noteId,
    participantId,
  });
  const { mutateAsync: createReply } = useJournalCreateReplyMutation({
    noteId,
    participantId,
    user,
  });

  const { mutateAsync: markNoteAsRead } = useJournalMarkNoteAsReadMutation({
    noteId,
    participantId,
  });

  const handleEditNote = async ({ text }: { text: string }) => {
    const noteId = editedNote?.noteId ?? '';
    setEditedNote(null);

    await editNote({ text, noteId });
  };

  const handleEditReply = async ({ text }: { text: string }) => {
    const replyId = editedReply?.replyId ?? '';
    setEditedReply(null);

    await editReply({ text, replyId });

    sendNoteReplyEditedAnalyticsEvent();
  };

  const handleCreateReply = async ({ text }: { text: string }) => {
    await createReply({ text });

    sendNoteReplyAddedAnalyticsEvent();
  };

  useEffect(() => {
    if (note && !note.isRead) {
      markNoteAsRead();
    }
  }, [note, markNoteAsRead]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Flex
      height="100%"
      flexDirection="column"
      justifyContent="space-between"
      alignItems="stretch"
      data-testid="journal-item-detailed-sidebar-body"
    >
      <Box style={{ flexGrow: 1, overflow: 'auto' }}>
        {note && Object.keys(note).length > 0 && (
          <Box
            w="100%"
            p="0.5rem 0.5rem 0.5rem 1rem"
            position="relative"
            data-testid="journal-item-detailed-card"
          >
            <JournalRepliesCard
              participantId={participantId}
              note={note}
              setEditedNode={() => setEditedNote(note)}
            />
            {!note.isRead && (
              <JournalCardUnreadIndicator
                options={{
                  left: '0.25rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />
            )}
          </Box>
        )}
        <Show when={replies && Boolean(replies.length)}>
          <For each={replies!}>
            {(reply) => {
              const isAuthor = reply.author.userId === user.userId;

              return (
                <JournalReply
                  key={reply.replyId}
                  date={reply.createdAt}
                  pictureUrl={reply.author.picture.url}
                  text={reply.text}
                  participantId={participantId}
                  userName={reply.author.fullName}
                  replyId={reply.replyId}
                  isAuthor={isAuthor}
                  setEditedReply={() => setEditedReply(reply)}
                  relatedNoteId={note!.noteId}
                >
                  <JournalReplyCardReactions
                    participantId={participantId}
                    replyId={reply.replyId}
                    noteId={reply.noteId}
                    reactions={reply.reactions}
                    user={user}
                  />
                </JournalReply>
              );
            }}
          </For>
        </Show>
      </Box>
      <Box
        position="sticky"
        bottom="0"
        p="1rem"
        boxShadow="0 -2px 4px rgba(0, 0, 0, 0.1)"
      >
        {editedReply && (
          <JournalForm
            text={editedReply.text}
            onSave={handleEditReply}
            placeholderText="Edit reply text..."
          >
            <JournalFormHeader onCancel={() => setEditedReply(null)}>
              <JournalFormHeaderTitle>Editing reply</JournalFormHeaderTitle>
            </JournalFormHeader>
          </JournalForm>
        )}
        {editedNote && (
          <JournalForm
            text={editedNote.text}
            onSave={handleEditNote}
            placeholderText="Edit note text..."
          >
            <JournalFormHeader onCancel={() => setEditedNote(null)}>
              <JournalFormHeaderTitle>Editing note</JournalFormHeaderTitle>
            </JournalFormHeader>
          </JournalForm>
        )}
        {!editedReply && !editedNote && (
          <JournalForm
            onSave={handleCreateReply}
            placeholderText="Write a reply..."
          />
        )}
      </Box>
    </Flex>
  );
};
